import React, { useCallback, useEffect, useState } from 'react';
import { Lock } from 'lucide-react'; 

// 📂 Context Imports
import { useAuth } from '../../../../../context/AuthContext';
import { useStore } from '../../../../../context/StoreContext';
import styles from './PackagesManager.module.css';
import PackagesForm from './components/PackagesForm/PackagesForm';
import PackagesList from './components/PackagesList/PackagesList';

const API_BASE = 'http://localhost:5001/api/v1/smiify/sm/packages';

export default function PackagesManager() {
    const [view, setView] = useState('LIST'); 
    const [packages, setPackages] = useState([]);
    const [editData, setEditData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const { user, hasPermission } = useAuth();
    const { activeStore } = useStore();
    const storeId = activeStore?.id || activeStore?._id || '';
    const partnerId = user?.partnerId || user?.partner || user?._id || '';

    // 🚀 100% BULLETPROOF GRANULAR PERMISSIONS
    const isSuperAdmin = ['SUPERADMIN', 'ADMIN'].includes(user?.role);
    const canViewPackages = isSuperAdmin || hasPermission('packages', 'view');
    const canCreatePackages = isSuperAdmin || hasPermission('packages', 'create');
    const canEditPackages = isSuperAdmin || hasPermission('packages', 'edit');
    const canDeactivatePackages = isSuperAdmin || hasPermission('packages', 'deactivate');

    const fetchPackages = useCallback(async () => {
        if (!partnerId || !storeId || storeId === 'global_hq' || !canViewPackages) return;
        setIsLoading(true);
        try {
            const token = localStorage.getItem('smiify_token');
            const res = await fetch(`${API_BASE}?storeId=${storeId}&partnerId=${partnerId}`, { headers: { Authorization: `Bearer ${token}` } });
            const result = await res.json();
            if (res.ok && result.success) setPackages(result.data);
        } catch (err) { console.error(err); } finally { setIsLoading(false); }
    }, [partnerId, storeId, canViewPackages]);

    useEffect(() => { 
        if(canViewPackages) fetchPackages(); 
    }, [fetchPackages, canViewPackages]);

    // 🔴 STRICT BLOCKER: View access check
    if (!canViewPackages) {
        return (
            <div className={styles.fullPageCenter}>
                <div className={styles.accessDeniedState}>
                    <div className={styles.lockIcon}><Lock size={36} /></div>
                    <h3>Access Restricted</h3>
                    <p>You do not have the required clearance to view the Service Packages catalog.</p>
                </div>
            </div>
        );
    }

    const handleSavePackage = async (payload) => {
        try {
            const token = localStorage.getItem('smiify_token');
            const res = await fetch(`${API_BASE}/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ ...payload, partnerId, storeId })
            });
            const result = await res.json();
            if (!res.ok || !result.success) throw new Error(result.message);
            await fetchPackages();
            setView('LIST');
        } catch (error) { window.alert(error.message); }
    };

    const handleToggleStatus = async (packageId) => {
        if (!canDeactivatePackages) return window.alert("Permission Denied: You cannot change package status.");
        try {
            const token = localStorage.getItem('smiify_token');
            await fetch(`${API_BASE}/toggle-status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ packageId, partnerId, storeId })
            });
            await fetchPackages();
        } catch (error) { window.alert('Failed to toggle status.'); }
    };

    return (
        <div className={styles.managerWrapper}>
            {view === 'LIST' && (
                <div className={styles.viewContainer}>
                    <PackagesList
                        packagesList={packages} 
                        isLoading={isLoading}
                        // 🚀 Passing permissions to child so it can hide/disable buttons natively
                        canCreate={canCreatePackages}
                        canEdit={canEditPackages}
                        canDeactivate={canDeactivatePackages}
                        onAddNew={() => { 
                            if(canCreatePackages) { setEditData(null); setView('FORM'); }
                            else window.alert("Permission Denied: You cannot create packages.");
                        }} 
                        onEdit={(pkg) => { 
                            if(canEditPackages) { setEditData(pkg); setView('FORM'); }
                            else window.alert("Permission Denied: You cannot edit packages.");
                        }} 
                        onToggleStatus={handleToggleStatus}
                    />
                </div>
            )}
            
            {/* 🔴 DOUBLE CHECK: Prevent rendering form if they somehow bypass the button */}
            {view === 'FORM' && ((!editData && canCreatePackages) || (editData && canEditPackages)) && (
                <div className={styles.viewContainer}>
                    <PackagesForm
                        initialData={editData} 
                        onCancel={() => { setView('LIST'); setEditData(null); }} 
                        onSave={handleSavePackage} 
                    />
                </div>
            )}
        </div>
    );
}